import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { DatePicker } from '@/components/ui/DatePicker'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { IntegratedFileSelect } from '@/components/ui/IntegratedFileSelect'
import { SectionInfoIcon } from '@/components/ui/SectionInfoIcon'
import { showError } from '@/lib/utils/notifications'
import { stockAddFormSchema, type StockAddFormValues } from '../types/schemas'
import type { StockAddModalProps} from '../types'
import { searchCompaniesForStock } from '../services/stockRegistryService'
import { cn } from '@/lib/utils/helpers'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'

const MAX_FILE_SIZE_MB = 5

/**
 * Yes / No as paired buttons (Angular `chec-radio`). Label stacked above buttons so two-column grids never overlap.
 */
function StockYesNoButtons(props: {
  id: string
  label: string
  /** Angular: unset (`''`) until user taps Yes or No. */
  value: '0' | '1' | ''
  onChange: (next: '0' | '1') => void
}) {
  const { id, label, value, onChange } = props
  const labelId = `${id}-label`
  return (
    <div className="min-w-0 space-y-2">
      <span id={labelId} className="block text-sm font-medium text-slate-700">
        {label}
      </span>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby={labelId}>
        <button
          type="button"
          role="radio"
          aria-checked={value === '1'}
          onClick={() => onChange('1')}
          className={cn(
            'min-w-[4rem] rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
            value === '1'
              ? 'border-button bg-button text-white'
              : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50',
          )}
        >
          Yes
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={value === '0'}
          onClick={() => onChange('0')}
          className={cn(
            'min-w-[4rem] rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
            value === '0'
              ? 'border-button bg-button text-white'
              : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50',
          )}
        >
          No
        </button>
      </div>
    </div>
  )
}

/** Shared `DatePicker` (react-datepicker) settings — same component as applicant About / Income. */
const STOCK_FORM_DATE_PICKER_PROPS = {
  allowFutureDates: true,
  showHelperText: false,
  popperFixed: true,
  popperPlacement: 'top-start' as const,
  placeholder: 'MM/DD/YYYY',
}

function todayISO(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function StockAddModal({ isOpen, submitting, onClose, onSubmit }: StockAddModalProps) {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<StockAddFormValues>({
    resolver: zodResolver(stockAddFormSchema),
    mode: 'onChange',
    defaultValues: {
      company_id: '',
      stock_class: 'B',
      share_count: '1',
      issued_date: todayISO(),
      effective_date: todayISO(),
      /** Angular `FormControl('')` — no radio selected until user chooses. */
      generate_letter: '',
      generate_email: '',
      /** Angular `FormControl('')` — override checkboxes start unchecked. */
      issued_override: false,
      effective_override: false,
      /** Angular `FormControl('0')` — No. */
      upload_certificate: '0',
      certificate_name: '',
    },
  })

  const [companyOptions, setCompanyOptions] = useState<SearchableSelectOption[]>([])
  const [companyLoading, setCompanyLoading] = useState(false)
  const fileRefCurrent = useRef<File | null>(null)

  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form watch() is intentionally unmemoizable
  const uploadCertificate = watch('upload_certificate')
  const certificateDisplayName = watch('certificate_name')
  const issuedOverride = watch('issued_override')
  const effectiveOverride = watch('effective_override')

  useEffect(() => {
    if (!isOpen) return
    reset({
      company_id: '',
      stock_class: 'B',
      share_count: '1',
      issued_date: todayISO(),
      effective_date: todayISO(),
      generate_letter: '',
      generate_email: '',
      issued_override: false,
      effective_override: false,
      upload_certificate: '0',
      certificate_name: '',
    })
    fileRefCurrent.current = null

    setCompanyLoading(true)
    searchCompaniesForStock()
      .then((opts) => {
        setCompanyOptions(
          opts.map((o) => ({ value: String(o.value), label: o.label })),
        )
        if (opts.length === 1) {
          setValue('company_id', String(opts[0].value), { shouldValidate: true })
        }
      })
      .catch((err) => showError(err instanceof Error ? err.message : 'Failed to load companies'))
      .finally(() => setCompanyLoading(false))
  }, [isOpen, reset])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      fileRefCurrent.current = null
      setValue('certificate_name', '', { shouldValidate: true, shouldDirty: true })
      return
    }
    const sizeMB = file.size / 1024 / 1024
    if (sizeMB > MAX_FILE_SIZE_MB) {
      showError(`File size exceeds allowable limit of ${MAX_FILE_SIZE_MB} MB.`)
      fileRefCurrent.current = null
      setValue('certificate_name', '', { shouldValidate: true, shouldDirty: true })
      return
    }
    fileRefCurrent.current = file
    setValue('certificate_name', file.name, { shouldValidate: true, shouldDirty: true })
  }

  const internalSubmit = (values: StockAddFormValues) => {
    const letter = values.generate_letter as '0' | '1'
    const email = values.generate_email as '0' | '1'
    onSubmit({
      company_id: Number(values.company_id),
      stock_class: values.stock_class,
      share_count: Number(values.share_count),
      issued_date: values.issued_date,
      effective_date: values.effective_date,
      generate_letter: letter,
      generate_email: email,
      ...(values.issued_override ? { issued_override: '1' } : {}),
      ...(values.effective_override ? { effective_override: '1' } : {}),
      upload_certificate: values.upload_certificate,
      certificate:
        values.upload_certificate === '1' && fileRefCurrent.current
          ? fileRefCurrent.current
          : undefined,
    })
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Add Certificate"
      onClose={onClose}
      maxWidth="max-w-4xl"
    >
      <form onSubmit={handleSubmit(internalSubmit)} className="space-y-4 p-2">
        {/* Angular: company + stock class each full-width in one column (stacked `col-lg-5` rows). */}
        <div className="flex flex-col gap-4">
          <div className="w-full min-w-0">
            <label className="mb-1 block text-sm font-medium text-slate-700">Select Company</label>
            <Controller
              name="company_id"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <SearchableSelect
                  options={companyOptions}
                  value={value}
                  onChange={(next) => onChange(Array.isArray(next) ? next[0] ?? '' : next)}
                  onBlur={onBlur}
                  placeholder={companyLoading ? 'Loading companies...' : 'Select Company'}
                  searchPlaceholder="Search By Company Name"
                  className="w-full"
                  error={errors.company_id?.message}
                />
              )}
            />
          </div>

          <div className="w-full min-w-0">
            <label className="mb-1 block text-sm font-medium text-slate-700">Stock Class</label>
            <select
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-button"
              {...register('stock_class')}
            >
              <option value="A">Class A</option>
              <option value="B">Class B</option>
              <option value="C">Class C</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              className="mb-1 block text-sm font-medium text-slate-700"
              htmlFor="stock-issued-date"
            >
              Issued Date
            </label>
            <Controller
              name="issued_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  id="stock-issued-date"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.issued_date?.message}
                  disabled={!issuedOverride}
                  {...STOCK_FORM_DATE_PICKER_PROPS}
                />
              )}
            />
            <div className="mt-2 flex items-center gap-2">
              <Controller
                name="issued_override"
                control={control}
                render={({ field: { onChange, onBlur, name, ref, value } }) => (
                  <input
                    ref={ref}
                    name={name}
                    onBlur={onBlur}
                    type="checkbox"
                    id="issued_override"
                    className="h-4 w-4 rounded border-slate-300 text-button accent-button focus:ring-button"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
              <label htmlFor="issued_override" className="text-sm text-slate-700">
                Override
              </label>
            </div>
          </div>

          <div>
            <label
              className="mb-1 block text-sm font-medium text-slate-700"
              htmlFor="stock-effective-date"
            >
              Effective Date
            </label>
            <Controller
              name="effective_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  id="stock-effective-date"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.effective_date?.message}
                  disabled={!effectiveOverride}
                  {...STOCK_FORM_DATE_PICKER_PROPS}
                />
              )}
            />
            <div className="mt-2 flex items-center gap-2">
              <Controller
                name="effective_override"
                control={control}
                render={({ field: { onChange, onBlur, name, ref, value } }) => (
                  <input
                    ref={ref}
                    name={name}
                    onBlur={onBlur}
                    type="checkbox"
                    id="effective_override"
                    className="h-4 w-4 rounded border-slate-300 text-button accent-button focus:ring-button"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
              <label htmlFor="effective_override" className="text-sm text-slate-700">
                Override
              </label>
            </div>
          </div>
        </div>

        <div className="w-full min-w-0">
          <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="stock-share-count">
            Share Issued
          </label>
          <input
            id="stock-share-count"
            type="number"
            min={1}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-button"
            {...register('share_count')}
          />
          {errors.share_count && (
            <span role="alert" className="mt-1 block text-xs text-red-600">
              {errors.share_count.message}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-x-8">
          <Controller
            name="generate_letter"
            control={control}
            render={({ field }) => (
              <StockYesNoButtons
                id="stock-generate-letter"
                label="Generate Letter"
                value={
                  field.value === '1' ? '1' : field.value === '0' ? '0' : ''
                }
                onChange={(v) => field.onChange(v)}
              />
            )}
          />
          <Controller
            name="generate_email"
            control={control}
            render={({ field }) => (
              <StockYesNoButtons
                id="stock-generate-email"
                label="Generate Email"
                value={
                  field.value === '1' ? '1' : field.value === '0' ? '0' : ''
                }
                onChange={(v) => field.onChange(v)}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-x-8 md:items-start">
          <Controller
            name="upload_certificate"
            control={control}
            render={({ field }) => (
              <StockYesNoButtons
                id="stock-upload-certificate"
                label="Upload Stock Certificate"
                value={field.value === '1' ? '1' : '0'}
                onChange={(v) => {
                  field.onChange(v)
                  if (v === '0') {
                    fileRefCurrent.current = null
                    setValue('certificate_name', '', { shouldValidate: true })
                  }
                }}
              />
            )}
          />
          {uploadCertificate === '1' ? (
            <div className="min-w-0 space-y-1 md:pt-0">
              <div
                className="mb-1 flex flex-wrap items-center gap-1.5"
                id="stock-cert-file-label"
              >
                <span className="text-sm font-medium text-slate-600">
                  Upload Stock Certificate
                </span>
                <SectionInfoIcon
                  content="Please upload a .pdf file"
                  aria-label="Certificate file requirements"
                  iconSize={18}
                  data-testid="stock-cert-upload-info"
                />
              </div>
              <div aria-labelledby="stock-cert-file-label">
                <IntegratedFileSelect
                  displayName={certificateDisplayName}
                  accept=".pdf,application/pdf"
                  emptyLabel="Choose file…"
                  browseLabel="Upload"
                  disabled={submitting}
                  onChange={handleFileChange}
                  data-testid="stock-cert-file"
                  className={cn(
                    errors.certificate_name &&
                      'border-red-500 focus-within:ring-2 focus-within:ring-red-500/30',
                  )}
                />
              </div>
              <span className="mt-1 block text-xs text-slate-500">
                PDF only (max {MAX_FILE_SIZE_MB} MB).
              </span>
              {errors.certificate_name && (
                <span role="alert" className="mt-1 block text-xs text-red-600">
                  {errors.certificate_name.message as string}
                </span>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap justify-center gap-2 border-t border-slate-200 pt-4 mt-4">
          <button
            type="submit"
            disabled={!isDirty || submitting}
            className={primaryEntityActionButtonClass}
          >
            {submitting ? 'Saving...' : 'Add'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={secondaryEntityActionButtonClass}
          >
            Cancel
          </button>
        </div>
      </form>
    </OnScreenModal>
  )
}
